























































































































































































































































import CAvatar from '~/components/shared/configurable/image/avatar/CAvatar.vue'
import {
  computed,
  defineComponent,
  ref,
  toRefs,
  watch
} from '@nuxtjs/composition-api'
import { Ref } from '@vue/composition-api'
import { useClassifiedForm } from '~/compositions/form'
import { formFieldProps } from '~/constants/form'
import { useDep } from '~/compositions/dependency-container'
import FormValidationBuilder from '~/builders/form/FormValidationBuilder'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'
import { LegacyUrlService } from '~/services/legacy/url/LegacyUrlService'
import { useNamespacedStore } from '~/compositions/store'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import CYesOrNoButtonGroup from '~/components/shared/configurable/form/checkbox/CYesOrNoButtonGroup.vue'
import { useI18n } from '~/compositions/i18n'
import { defineComponentTranslations } from '~/utils/i18n'
import { formatPrice } from '~/utils/number'
import CPrice from '~/components/shared/configurable/CPrice.vue'
import { useAppConfirmationModal } from '~/compositions/app-confirmation-modal'
import { ConfirmationModalOptions } from '~/store/modules/shared/app-confirmation-modal/state'

export default defineComponent({
  props: formFieldProps,
  components: {
    CPrice,
    CYesOrNoButtonGroup,
    FieldError,
    CAvatar
  },
  setup(props) {
    const { specs, name } = toRefs(props)
    const financeEnabled = ref<boolean>(false)
    const defaultValuesOverride = ref<boolean>(false)

    const interest = ref<number | null>(null)
    const interestError = ref<string | null>(null)

    const minDownpayment = ref<number | null>(null)
    const minDownpaymentError = ref<string | null>(null)

    const maxInstallments = ref<number | null>(null)
    const maxInstallmentsError = ref<string | null>(null)

    const legacyUrlService = useDep(LegacyUrlService)
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)

    const { state } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
    const { t } = useI18n()
    const confirm = useAppConfirmationModal()

    const onlineFinanceEnabled = computed(() =>
      Boolean(state.fieldValues.online_finance?.accepts_online_financing)
    )

    const classifiedPrice = computed(() => {
      if (state.fieldValues.price && Number(state.fieldValues.price)) {
        return Number(state.fieldValues.price)
      }
      return null
    })

    watch(onlineFinanceEnabled, val => {
      if (financeEnabled.value && val) {
        handleFinanceEnabledChange(false, true)
      }
    })

    function onValue(v: Ref<any>) {
      financeEnabled.value = Boolean(v.value?.finance)
      interest.value =
        v.value?.interest || v.value?.interest === 0 ? v.value?.interest : null
      minDownpayment.value =
        v.value?.min_down_payment || v.value?.min_down_payment === 0
          ? v.value?.min_down_payment
          : null
      maxInstallments.value =
        v.value?.max_installments || v.value?.max_installments === 0
          ? v.value?.max_installments
          : null

      if (interest.value || minDownpayment.value || maxInstallments.value) {
        defaultValuesOverride.value = true
      }
    }

    function onError(e: Ref<any>) {
      interestError.value = e.value?.interest || null
      minDownpaymentError.value = e.value?.min_down_payment || null
      maxInstallmentsError.value = e.value?.max_installments || null
    }
    const { valueChanged, debouncedValueChanged } = useClassifiedForm(props, {
      onValue,
      onError
    })
    const formValidationBuilder = useDep(FormValidationBuilder)

    const closeAndClear = () => {
      onEraseCustomValuesConfirm()
      debouncedValueChanged(name.value, null)
    }
    const handleFinanceEnabledChange = (accepts: boolean, force: boolean) => {
      financeEnabled.value = accepts
      if (!accepts) {
        if (!force && defaultValuesOverride.value && hasCustomValues.value) {
          confirm.show({
            title: t('warning'),
            text: t('form_values_deletion_confirm'),
            confirmButtonText: t('yes'),
            centered: true,
            size: 'md',
            onConfirm: closeAndClear,
            onCancel: () => {
              financeEnabled.value = true
            }
          } as ConfirmationModalOptions)
        } else {
          financeEnabled.value = accepts
          closeAndClear()
        }
      } else {
        emitValues()
      }
    }

    const validateInterest = (text: string) => {
      const textAsNum = Number(text)
      formValidationBuilder
        .new(interestError)
        .required(specs.value?.interest.required, textAsNum)
        .aboveMinNumber(specs.value?.interest.min, textAsNum)
        .belowMaxNumber(specs.value?.interest.max, textAsNum)
        .validate()
    }

    const handleInterestInput = (text: string) => {
      validateInterest(text)
      emitValues()
    }

    const validateMinDownpayment = (text: string) => {
      const textAsNum = Number(text)
      formValidationBuilder
        .new(minDownpaymentError)
        .required(specs.value?.min_down_payment.required, textAsNum)
        .aboveMinNumber(specs.value?.min_down_payment.min, textAsNum)
        .belowMaxNumber(specs.value?.min_down_payment.max, textAsNum)
        .validate()
    }

    const handleMinDownpaymentInput = (text: string) => {
      validateMinDownpayment(text)
      emitValues()
    }
    const validateMaxInstallments = (text: string) => {
      const textAsNum = Number(text)
      formValidationBuilder
        .new(maxInstallmentsError)
        .required(specs.value?.max_installments.required, textAsNum)
        .isNumeric(textAsNum)
        .aboveMinNumber(specs.value?.max_installments.min, textAsNum)
        .belowMaxNumber(specs.value?.max_installments.max, textAsNum)
        .validate()
    }

    const handleMaxInstallmentsInput = (text: string) => {
      validateMaxInstallments(text)
      emitValues()
    }

    const emitValues = () => {
      if (!financeEnabled.value) {
        valueChanged('is_financed', false)
        debouncedValueChanged(name.value, null)
        return
      }
      valueChanged('is_financed', true)
      debouncedValueChanged(name.value, {
        finance: true,
        interest:
          interest.value || interest.value === 0
            ? Number(interest.value)
            : null,
        min_down_payment:
          minDownpayment.value || minDownpayment.value === 0
            ? Number(minDownpayment.value)
            : null,
        max_installments:
          maxInstallments.value || maxInstallments.value === 0
            ? Number(maxInstallments.value)
            : null
      })
    }

    const storeDownpaymentAmount = computed(() => {
      if (
        classifiedPrice.value !== null &&
        specs.value.min_down_payment.default
      ) {
        return formatPrice(
          (specs.value.min_down_payment.default * classifiedPrice.value) / 100
        ).toString()
      }
      return ''
    })
    const overrideDownpaymentAmount = computed(() => {
      if (classifiedPrice.value !== null && minDownpayment.value) {
        return formatPrice(
          (minDownpayment.value * classifiedPrice.value) / 100
        ).toString()
      }
      return ''
    })

    const hasCustomValues = computed(() =>
      Boolean(
        interest.value ||
          interest.value === 0 ||
          minDownpayment.value ||
          minDownpayment.value === 0 ||
          maxInstallments.value ||
          maxInstallments.value === 0
      )
    )

    const handleOverrideChange = (e: Event) => {
      if (defaultValuesOverride.value && hasCustomValues.value) {
        e.preventDefault()
        e.stopPropagation()
        confirm.show({
          title: t('warning'),
          text: t('restore_outlet_values_confirm'),
          confirmButtonText: t('yes'),
          centered: true,
          size: 'md',
          onConfirm: onEraseCustomValuesConfirm,
          onCancel: onEraseCustomValuesCancel
        } as ConfirmationModalOptions)
      }
    }

    const storeUrl = computed(() => {
      return legacyUrlService.getAccountStoresUrl(
        userGetters('userDealerIds').length > 1 && specs.value?.old_dealer_id
          ? `#dealer-${specs.value.old_dealer_id}`
          : '#finance'
      )
    })

    const onEraseCustomValuesConfirm = () => {
      defaultValuesOverride.value = false

      interest.value = null
      minDownpayment.value = null
      maxInstallments.value = null

      interestError.value = null
      minDownpaymentError.value = null
      maxInstallmentsError.value = null

      emitValues()
    }

    const onEraseCustomValuesCancel = () => {
      defaultValuesOverride.value = true
    }

    return {
      financeEnabled,
      handleFinanceEnabledChange,
      defaultValuesOverride,
      interest,
      interestError,
      minDownpayment,
      minDownpaymentError,
      maxInstallments,
      maxInstallmentsError,
      emitValues,
      handleInterestInput,
      handleMinDownpaymentInput,
      handleMaxInstallmentsInput,
      storeUrl,
      onEraseCustomValuesConfirm,
      handleOverrideChange,
      onlineFinanceEnabled,
      onEraseCustomValuesCancel,
      storeDownpaymentAmount,
      overrideDownpaymentAmount,
      t
    }
  },
  i18n: defineComponentTranslations({
    do_you_offer_finance: {
      en: 'Do you offer finance?',
      el: 'Διαθέτεις επιλογή χρηματοδότησης;'
    },
    interest_rate_placeholder: {
      en: 'Interest rate percentage',
      el: 'Ποσοστό επιτοκίου'
    },
    minimum_downpayment_placeholder: {
      en: 'Downpayment percentage',
      el: 'Ποσοστό προκαταβολής'
    },
    maximum_installments_placeholder: {
      en: 'Number of installments',
      el: 'Αριθμός δόσεων'
    },
    restore_outlet_values_confirm: {
      en:
        'This action will erase the values you have entered and revert back to the outlet values for this classified, do you wish to continue?',
      el:
        'Αυτή η ενέργεια θα διαγράψει τις τιμές που έχεις εισάγει και θα επαναφέρει της τιμές καταστήματος για την αγγελία, θέλεις να συνεχίσεις'
    },
    final_amount_based_on_price: {
      en: 'Amount (based on price)',
      el: 'Ποσό (βάση τιμής)'
    }
  })
})
